import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { fetchPublicContent, api, userApi } from "../lib/api";
import { Nav } from "../components/Nav";
import { AudioPlayer } from "../components/AudioPlayer";
import {
  Hero, Manifeste, Univers, Experiences, Evenements, Sound, People, Collaborer, Contact, Footer,
} from "../components/PublicSections";

function ActiveBenefits() {
  const [b, setB] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem("eff_user_token")) return;
    userApi.get("/user/benefits").then(({ data }) => { if (data.active) setB(data); }).catch(() => {});
  }, []);
  if (!b) return null;
  return (
    <section data-testid="active-benefits" className="border-b eff-line py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="eff-eyebrow mb-2">Avantages Actifs</div>
          <h2 className="font-display text-2xl md:text-3xl">Membre · {b.tier}</h2>
        </div>
        <a href="/access" data-testid="active-benefits-access" className="eff-btn">Mon espace</a>
      </div>
      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-3">
        {b.benefits.map((x, i) => (
          <li key={i} data-testid={`active-benefit-${i}`} className="flex gap-2 text-[13px] text-[#bbb]">
            <Check className="w-4 h-4 shrink-0 mt-0.5" /> {x}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function PublicSite() {
  const [data, setData] = useState(null);
  const params = new URLSearchParams(window.location.search);
  const wantPreview = params.get("preview") === "1";
  const deepTrack = params.get("track") || "";
  const deepPlaylist = params.get("playlist") || "";

  useEffect(() => {
    document.documentElement.classList.add("dark");
    const token = localStorage.getItem("eff_token");
    if (wantPreview && token) {
      api.get("/admin/preview/content").then(({ data }) => setData(data))
        .catch(() => fetchPublicContent().then(setData).catch(() => setData({ error: true })));
    } else {
      fetchPublicContent().then(setData).catch(() => setData({ error: true }));
    }
  }, [wantPreview]);

  useEffect(() => { if (data?.settings?.seo?.title) document.title = data.settings.seo.title; }, [data]);

  if (!data) return <div className="min-h-screen bg-[#080808]" />;
  if (data.error) return <div className="min-h-screen bg-[#080808] flex items-center justify-center text-[#a6a6a6]">Contenu indisponible.</div>;

  const s = data.settings || {};
  const isPreview = !!data.preview;
  const enabled = new Set((data.sections || []).filter((x) => x.enabled).map((x) => x.key));
  const order = (data.sections || []).map((x) => x.key);
  const has = (k) => isPreview || enabled.size === 0 || enabled.has(k);

  const render = {
    hero: <Hero key="hero" settings={s} />,
    manifeste: <Manifeste key="manifeste" settings={s} />,
    univers: <Univers key="univers" items={data.universe} />,
    experiences: <Experiences key="experiences" items={data.experiences} />,
    evenements: <Evenements key="evenements" items={data.events} />,
    sound: <Sound key="sound" tracks={data.audio_tracks} playlists={data.playlists} deepTrack={deepTrack} deepPlaylist={deepPlaylist} />,
    collaborateurs: <People key="collaborateurs" artists={data.artists} collaborators={data.collaborators} />,
    collaborer: <Collaborer key="collaborer" settings={s} />,
    contact: <Contact key="contact" settings={s} />,
    footer: <Footer key="footer" settings={s} />,
  };
  const keys = order.length ? order : Object.keys(render);

  return (
    <div className="bg-[#080808] min-h-screen pb-24">
      {isPreview && (
        <div data-testid="preview-banner" className="fixed top-0 left-0 right-0 z-[60] bg-white text-black text-[10px] tracking-[0.25em] uppercase text-center py-2">
          Mode aperçu — brouillons visibles · non publié
        </div>
      )}
      <div className={isPreview ? "pt-9" : ""}>
        <Nav brand={s.brand} logo={s.logo_svg} tracks={data.audio_tracks || []} />
        <div className="max-w-[1180px] mx-auto px-7">
          <ActiveBenefits />
          {keys.filter(has).map((k) => render[k]).filter(Boolean)}
        </div>
      </div>
      <AudioPlayer />
    </div>
  );
}
